import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "next-themes";
import api from "@/lib/api";
import { T, F, th as palette, I, Over, Btn } from "@/lib/bricks";
import { Stats, StatusText, TextLink } from "@/components/calm/Calm";
import { fmtDay } from "@/lib/dates";
import { useAuth } from "@/contexts/AuthContext";
import BricksLoader from "@/components/BricksLoader";
import { bookShootHref } from "@/lib/navConfig";
import { BookMediaLink } from "@/components/listing/BookMedia";
import { loadLayout, saveLayout, sanitizeLayout, move, removeAt, addWidget, cycleSpan, money, shortDate,
  parseGoal, loadGoal, saveGoal, goalProgress } from "./deskLayout";

/**
 * The desk — role-aware Overview (office / team / solo), as shown on www.brxs.ca.
 * Every number comes from GET /dashboard/desk (the account's own deals,
 * commissions, shoots, follow-ups, trust ledger, contacts, e-sign envelopes and
 * MLS feed). Layout is the user's:
 * reorder, resize, remove and add widgets; saved in this browser.
 */

function useWide(min = 1024) {
  const get = () => (typeof window === "undefined" ? true : window.innerWidth >= min);
  const [wide, setWide] = useState(get);
  useEffect(() => {
    const on = () => setWide(window.innerWidth >= min);
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, [min]);
  return wide;
}

function fmtKpi(k) {
  if (k.format === "money") return money(k.value);
  return String(k.value ?? 0);
}

const Empty = ({ p, children }) => <div style={{ fontSize: 13, color: p.ink3, padding: "6px 0" }}>{children}</div>;

function Row({ p, last, children, onClick }) {
  return (
    <div onClick={onClick} style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, padding: "10px 0",
      borderBottom: last ? "none" : `1px solid ${p.hair}`, cursor: onClick ? "pointer" : "default" }}>
      {children}
    </div>
  );
}

const mono = (p, extra = {}) => ({ fontFamily: F.mono, fontSize: 12, color: p.ink3, ...extra });

function WVolume({ d, p }) {
  const s = d.series || [];
  const max = Math.max(1, ...s.map((x) => x.jobs));
  const path = s.map((x, i) => `${i === 0 ? "M" : "L"}${(i / Math.max(1, s.length - 1)) * 560},${80 - (x.jobs / max) * 70}`).join(" ");
  const ticks = [0, 10, 20, 29].map((i) => s[i]).filter(Boolean);
  return (
    <>
      <div style={{ fontFamily: F.mono, fontSize: 24, letterSpacing: -0.5 }}>{d.total} <span style={{ color: p.ink3, fontSize: 13 }}>jobs</span></div>
      <svg viewBox="0 0 560 92" preserveAspectRatio="none" style={{ width: "100%", height: 96, marginTop: 12, display: "block" }} aria-label="Jobs per day, last 30 days">
        {s.length > 1 && <path d={`${path} L560,92 L0,92 Z`} fill={p.ink} opacity="0.05" />}
        {s.length > 1 && <path d={path} stroke={p.ink2} strokeWidth="1.5" fill="none" vectorEffect="non-scaling-stroke" />}
      </svg>
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6, fontFamily: F.mono, fontSize: 10, color: p.ink4 }}>
        {ticks.map((t) => <span key={t.date}>{shortDate(t.date)}</span>)}
      </div>
    </>
  );
}

function WToday({ d, p }) {
  if (!d.shoots?.length) return <Empty p={p}>No shoots today.</Empty>;
  return d.shoots.map((s, i) => (
    <Row key={s.job_id || i} p={p} last={i === d.shoots.length - 1}>
      <span style={mono(p, { color: p.ink, width: 44, flexShrink: 0 })}>{s.time || "Today"}</span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, overflowWrap: "anywhere" }}>{s.address || "Shoot"}</div>
        <div style={{ fontSize: 11, color: p.ink3, marginTop: 2 }}>{s.agent}</div>
      </span>
      {s.status ? <StatusText>{humanizeStatus(s.status)}</StatusText> : null}
    </Row>
  ));
}

function WPipeline({ d, p }) {
  return (
    <>
      {(d.stages || []).map((r) => (
        <Row key={r.stage} p={p}>
          <span style={{ fontSize: 13 }}>{r.label}</span>
          <span style={{ display: "flex", gap: 16 }}>
            <span style={mono(p, { fontSize: 11, color: p.ink4, width: 64, textAlign: "right" })}>{r.deals} deal{r.deals !== 1 ? "s" : ""}</span>
            <span style={mono(p, { fontSize: 13, color: p.ink, width: 64, textAlign: "right" })}>{money(r.value)}</span>
          </span>
        </Row>
      ))}
      <div style={{ display: "flex", justifyContent: "space-between", paddingTop: 12 }}>
        <span style={{ fontSize: 12, color: p.ink3 }}>Open pipeline · {d.open_deals} deal{d.open_deals !== 1 ? "s" : ""}</span>
        <span style={mono(p, { fontSize: 14, color: p.ink })}>{money(d.open_value)}</span>
      </div>
    </>
  );
}

function WLeaderboard({ d, p }) {
  if (!d.agents?.length) return <Empty p={p}>No closings in the last 30 days.</Empty>;
  return d.agents.map((a, i) => (
    <Row key={a.agent_id} p={p} last={i === d.agents.length - 1}>
      <span style={mono(p, { width: 18, color: p.ink4 })}>{i + 1}</span>
      <span style={{ flex: 1, fontSize: 13 }}>{a.name}</span>
      <span style={mono(p, { fontSize: 11, width: 56, textAlign: "right" })}>{a.deals} closed</span>
      <span style={mono(p, { fontSize: 13, color: p.ink, width: 64, textAlign: "right" })}>{money(a.gci || a.volume)}</span>
    </Row>
  ));
}

function WTrust({ d, p, dark, go }) {
  return (
    <>
      <div style={{ fontFamily: F.mono, fontSize: 24, letterSpacing: -0.5 }}>{money(d.balance)}</div>
      <div style={{ fontSize: 11, color: p.ink3, marginTop: 4 }}>Approved trust balance</div>
      <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 8, fontSize: 12.5 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span>{d.due_period} reconciliation</span>
          {d.due_period_signed ? <StatusText>Signed</StatusText>
            : d.overdue ? <StatusText attention>Overdue</StatusText>
            : <StatusText>Due {shortDate(d.due_date)}</StatusText>}
        </div>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <span>Open shortfalls</span>
          <span style={mono(p, { color: d.open_shortfalls ? p.attn : p.ink })}>{d.open_shortfalls}</span>
        </div>
        <div style={{ color: p.ink3, fontSize: 11 }}>Last signed: {d.last_signed_period || "none yet"}</div>
      </div>
      <div onClick={() => go("/accounting")} style={{ marginTop: 12, fontSize: 12, color: p.ink, cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 3 }}>Open trust</div>
    </>
  );
}

function WClosings({ d, p, go }) {
  if (!d.closings?.length) return <Empty p={p}>No closings in the next 60 days.</Empty>;
  return d.closings.map((c, i) => (
    <Row key={c.transaction_id} p={p} last={i === d.closings.length - 1} onClick={() => go(`/transactions/${c.transaction_id}`)}>
      <span style={mono(p, { width: 48, flexShrink: 0, color: c.days <= 7 ? p.attn : p.ink3 })}>{shortDate(c.date)}</span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, overflowWrap: "anywhere" }}>{c.address || "Deal"}</div>
        {c.agent && <div style={{ fontSize: 11, color: p.ink3, marginTop: 2 }}>{c.agent}</div>}
      </span>
      <span style={mono(p, { color: p.ink })}>{money(c.value)}</span>
    </Row>
  ));
}

const MEDIA = { in_progress: ["In progress", "brick"], delivered: ["Delivered", "ok"], none: ["No shoot", "neutral"] };

function WListings({ d, p, dark, go }) {
  if (!d.listings?.length) return <Empty p={p}>No open listings or deals.</Empty>;
  return d.listings.map((l, i) => (
    <Row key={l.transaction_id} p={p} last={i === d.listings.length - 1} onClick={() => go(`/transactions/${l.transaction_id}`)}>
      <span style={{ flex: 1, minWidth: 0, fontSize: 13, overflowWrap: "anywhere" }}>{l.address || "Listing"}</span>
      <span style={mono(p, { fontSize: 11, textTransform: "capitalize" })}>{String(l.status || "").replace(/_/g, " ")}</span>
      <span style={mono(p, { color: p.ink, width: 64, textAlign: "right" })}>{money(l.value)}</span>
      <StatusText>{MEDIA[l.media][0]}</StatusText>
    </Row>
  ));
}

function WIncome({ d, p }) {
  const s = d.series || [];
  const max = Math.max(1, ...s.map((x) => x.amount));
  return (
    <>
      <div style={{ fontFamily: F.mono, fontSize: 24, letterSpacing: -0.5 }}>{money(d.ytd)}</div>
      <div style={{ fontSize: 11, color: p.ink3, marginTop: 4 }}>{d.basis === "agent_payout" ? "Your share, year to date" : "Gross commission, year to date"}</div>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 4, height: 64, marginTop: 14 }}>
        {s.map((m) => (
          <div key={m.month} title={`${m.month} · ${money(m.amount)}`} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
            <div style={{ width: "100%", height: Math.max(2, (m.amount / max) * 52), background: m.amount ? p.ink3 : p.hair, borderRadius: 1 }} />
            <span style={{ fontFamily: F.mono, fontSize: 9, color: p.ink4 }}>{m.month[0]}</span>
          </div>
        ))}
      </div>
    </>
  );
}

function WNextShoot({ d, p }) {
  const s = d.shoot;
  if (!s) return (
    <Empty p={p}>Nothing booked.</Empty>
  );
  return (
    <>
      <div style={{ fontFamily: F.mono, fontSize: 20, letterSpacing: -0.4 }}>{shortDate(s.date)}{s.time ? ` · ${s.time}` : ""}</div>
      <div style={{ fontSize: 13, marginTop: 8 }}>{s.address}</div>
      <div style={{ fontSize: 11, color: p.ink3, marginTop: 4, textTransform: "capitalize" }}>{[s.package, (s.status || "").replace(/_/g, " ")].filter(Boolean).join(" · ")}</div>
    </>
  );
}

function WFollowups({ d, p, dark, go }) {
  if (!d.tasks?.length) return <Empty p={p}>No follow-ups due this week.</Empty>;
  return d.tasks.map((t, i) => (
    <Row key={t.task_id} p={p} last={i === d.tasks.length - 1} onClick={() => go("/crm")}>
      <span style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, overflowWrap: "anywhere" }}>{t.title}</div>
        {t.contact && <div style={{ fontSize: 11, color: p.ink3, marginTop: 2 }}>{t.contact}</div>}
      </span>
      {t.overdue ? <StatusText attention>Overdue</StatusText> : <span style={mono(p)}>{shortDate(t.due)}</span>}
    </Row>
  ));
}


const humanizeStatus = (v) => { const t = String(v || "").replace(/_/g, " "); return t.charAt(0).toUpperCase() + t.slice(1); };
const STAGE_LABEL = { booked: "Booked", scheduled: "Scheduled", shooting: "On site", editing: "In post", other: "Other" };

function WInflight({ d, p, dark, go }) {
  if (!d.count) return <Empty p={p}>No shoots in progress.</Empty>;
  return (
    <>
      <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 6 }}>
        {(d.stages || []).map((s) => (
          <span key={s.stage} style={{ fontSize: 12, color: s.count ? p.ink2 : p.ink4 }}>
            <span style={mono(p, { fontSize: 14, color: s.count ? p.ink : p.ink4, marginRight: 5 })}>{s.count}</span>{s.label}
          </span>
        ))}
      </div>
      {(d.jobs || []).map((j, i) => (
        <Row key={j.job_id || i} p={p} last={i === d.jobs.length - 1} onClick={() => go("/jobs")}>
          <span style={mono(p, { width: 48, flexShrink: 0 })}>{j.date ? shortDate(j.date) : ""}</span>
          <span style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, overflowWrap: "anywhere" }}>{j.address || "Shoot"}</div>
            <div style={{ fontSize: 11, color: p.ink3, marginTop: 2 }}>{[j.time, j.package, j.agent].filter(Boolean).join(" · ")}</div>
          </span>
          <StatusText>{STAGE_LABEL[j.stage] || humanizeStatus(j.status)}</StatusText>
        </Row>
      ))}
    </>
  );
}

function WMls({ d, p, dark, go }) {
  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 14 }}>{d.connected ? (d.provider_label || "MLS feed") : "No feed connected"}</span>
        <StatusText>{d.connected ? (d.sandbox ? "Sandbox" : "Connected") : "Not connected"}</StatusText>
      </div>
      {d.connected && (
        <div style={{ fontSize: 11, color: p.ink3, marginTop: 6, lineHeight: 1.45 }}>
          {d.last_listing_sync_at ? `Synced ${fmtDay(d.last_listing_sync_at)}` : d.can_sync_listings ? "Not synced yet" : "Add your office MLS ID to sync"}
        </div>
      )}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 8, marginTop: 14 }}>
        {(d.listings || []).map((r) => (
          <div key={r.status}>
            <div style={mono(p, { fontSize: 18, color: r.count ? p.ink : p.ink4 })}>{r.count}</div>
            <div style={{ fontSize: 10.5, color: p.ink3, textTransform: "capitalize", marginTop: 2 }}>{r.status}</div>
          </div>
        ))}
      </div>
      <div onClick={() => go(d.connected ? "/mls" : "/integrations")} style={{ marginTop: 12, fontSize: 12, color: p.ink, cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 3 }}>
        {d.connected ? "Open MLS listings" : "Connect a feed"}
      </div>
    </>
  );
}

function WLeadSources({ d, p, go }) {
  if (!d.total) return <Empty p={p}>No new contacts in the last {d.days} days.</Empty>;
  const max = Math.max(1, ...(d.sources || []).map((s) => s.count));
  return (
    <>
      <div style={{ fontFamily: F.mono, fontSize: 24, letterSpacing: -0.5 }}>{d.total} <span style={{ color: p.ink3, fontSize: 13 }}>new contacts</span></div>
      <div style={{ display: "flex", flexDirection: "column", gap: 9, marginTop: 14 }}>
        {(d.sources || []).map((s) => (
          <div key={s.source} onClick={() => go("/crm")} style={{ cursor: "pointer" }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5 }}>
              <span>{s.source}</span>
              <span style={mono(p, { fontSize: 11 })}>{s.count} · {s.share}%</span>
            </div>
            <div style={{ height: 4, background: p.hair, borderRadius: 1, marginTop: 5 }}>
              <div style={{ width: `${(s.count / max) * 100}%`, height: "100%", background: p.ink3, borderRadius: 1 }} />
            </div>
          </div>
        ))}
        {d.other > 0 && <div style={{ fontSize: 11, color: p.ink3 }}>+{d.other} from other sources</div>}
      </div>
    </>
  );
}

function WGoal({ d, p, dark, userId, role }) {
  const [goal, setGoal] = useState(() => loadGoal(typeof window === "undefined" ? null : window.localStorage, userId, role));
  const [draft, setDraft] = useState("");
  const [editing, setEditing] = useState(false);
  const prog = goalProgress(goal, d.ytd, d.pace);
  const basis = d.basis === "agent_payout" ? "your share" : "gross commission";
  const commit = () => {
    const n = parseGoal(draft);
    if (!n) return;
    saveGoal(window.localStorage, userId, role, n);
    setGoal(n);
    setEditing(false);
  };
  if (!goal || editing) {
    return (
      <>
        <div style={{ fontSize: 13, color: p.ink2 }}>Set a yearly GCI goal ({basis}).</div>
        <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
          <input aria-label="Yearly GCI goal" inputMode="decimal" placeholder="e.g. 250k" value={draft}
            onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") commit(); }}
            style={{ flex: 1, minWidth: 0, padding: "9px 12px", border: `1px solid ${p.hairS}`, borderRadius: 2, background: p.bg, color: p.ink, fontFamily: F.mono, fontSize: 13 }} />
          <Btn variant="primary" dark={dark} onClick={commit} disabled={!parseGoal(draft)}>Save</Btn>
        </div>
        <div style={{ fontSize: 11, color: p.ink4, marginTop: 8 }}>{money(d.ytd)} so far this year</div>
      </>
    );
  }
  return (
    <>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8 }}>
        <span style={{ fontFamily: F.mono, fontSize: 24, letterSpacing: -0.5 }}>{money(d.ytd)}</span>
        <span style={mono(p, { fontSize: 12 })}>of {money(goal)}</span>
      </div>
      <div style={{ height: 6, background: p.hair, borderRadius: 1, marginTop: 12 }} role="progressbar" aria-valuenow={prog.pct} aria-valuemin={0} aria-valuemax={100}>
        <div style={{ width: `${prog.pct}%`, height: "100%", background: p.ink2, borderRadius: 1, transition: "width 400ms cubic-bezier(0.23, 1, 0.32, 1)" }} />
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 10, fontSize: 12 }}>
        <span style={{ color: p.ink3 }}>{prog.pct}% · {money(prog.remaining)} to go</span>
        <span style={{ color: p.ink3 }}>{d.ytd ? `Pace ${money(d.pace)}` : "No GCI yet"}</span>
      </div>
      <div style={{ fontSize: 11, color: p.ink4, marginTop: 8 }}>
        {money(d.mtd)} this month · {d.deals_closed_ytd} closed YTD ·{" "}
        <span onClick={() => { setDraft(String(goal)); setEditing(true); }} style={{ color: p.ink, cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 3 }}>Edit goal</span>
      </div>
    </>
  );
}

const SIGN = { completed: ["Signed", "ok"], sent: ["Waiting", "warn"], declined: ["Declined", "bad"], expired: ["Expired", "neutral"], voided: ["Voided", "neutral"] };

function WSignings({ d, p, dark, go }) {
  if (!d.signings?.length) return <Empty p={p}>No documents sent for signature yet.</Empty>;
  return (
    <>
      <div style={{ fontSize: 12, color: p.ink3, marginBottom: 4 }}>{d.completed_30d} signed · 30d · {d.waiting} waiting</div>
      {d.signings.map((s, i) => (
        <Row key={s.envelope_id} p={p} last={i === d.signings.length - 1} onClick={() => go(s.transaction_id ? `/transactions/${s.transaction_id}` : "/esign")}>
          <span style={mono(p, { width: 48, flexShrink: 0 })}>{shortDate(s.date)}</span>
          <span style={{ flex: 1, minWidth: 0, fontSize: 13, overflowWrap: "anywhere" }}>{s.title}{s.docusign ? " · DocuSign" : ""}</span>
          <StatusText attention={s.status === "declined"}>{(SIGN[s.status] || [humanizeStatus(s.status)])[0]}</StatusText>
        </Row>
      ))}
    </>
  );
}

const RENDER = { volume: WVolume, today: WToday, pipeline: WPipeline, leaderboard: WLeaderboard, trust: WTrust,
  closings: WClosings, listings: WListings, income: WIncome, nextshoot: WNextShoot, followups: WFollowups,
  inflight: WInflight, mls: WMls, leadsources: WLeadSources, goal: WGoal, signings: WSignings };

function IconBtn({ p, name, label, onClick, disabled }) {
  return (
    <button type="button" aria-label={label} title={label} onClick={onClick} disabled={disabled} className="bx-iconbtn"
      style={{ width: 36, height: 36, borderRadius: 2, border: `1px solid ${p.hair}`, background: p.bg, display: "inline-flex",
        alignItems: "center", justifyContent: "center", cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.35 : 1, padding: 0 }}>
      <I name={name} size={16} color={p.ink3} />
    </button>
  );
}

export default function DeskBoard({ actions = null, showHeader = true, bookAction = true }) {
  const [desk, setDesk] = useState(null);
  const [failed, setFailed] = useState(false);
  const [editing, setEditing] = useState(false);
  const [layout, setLayout] = useState([]);
  const { resolvedTheme } = useTheme();
  const dark = resolvedTheme === "dark";
  const p = palette(dark);
  const { user } = useAuth();
  const navigate = useNavigate();
  const wide = useWide();

  useEffect(() => {
    let live = true;
    api.get("/dashboard/desk")
      .then((r) => { if (!live) return; setDesk(r.data); })
      .catch(() => { if (live) setFailed(true); });
    return () => { live = false; };
  }, []);

  useEffect(() => {
    if (!desk) return;
    setLayout(loadLayout(window.localStorage, user?.id, desk.role, desk.available, desk.default_layout));
  }, [desk, user?.id]);

  const update = (next) => {
    const clean = sanitizeLayout(next, desk.available, []);
    setLayout(clean);
    saveLayout(window.localStorage, user?.id, desk.role, clean);
  };

  const library = useMemo(() => (desk ? desk.available.filter((k) => !layout.some(([x]) => x === k)) : []), [desk, layout]);

  if (failed) return null; // the rest of the overview still renders
  if (!desk) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 160 }}>
      <BricksLoader variant="cycle" size={28} color={T.brick} />
    </div>
  );

  const firstName = (user?.name || "").split(" ")[0] || "there";
  const go = (path) => navigate(path);

  return (
    <div data-testid="desk-board" style={{ display: "flex", flexDirection: "column", gap: 24, fontFamily: F.display, color: p.ink }}>
      {showHeader && (
        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
          <div>
            {editing ? (
              <>
                                <h1 style={{ fontSize: 30, fontWeight: 500, letterSpacing: -1, margin: 0 }}>Customize your desk</h1>
                <div style={{ fontSize: 13, color: p.ink2, marginTop: 6 }}>Saved on this device.</div>
              </>
            ) : (
              <>
                <h1 style={{ fontSize: "clamp(28px, 7vw, 34px)", fontWeight: 500, letterSpacing: -1.1, margin: 0 }}>{desk.greeting}, {firstName}.</h1>
                <div style={{ fontSize: 14, color: p.ink3, marginTop: 6 }}>{desk.headline}</div>
              </>
            )}
          </div>
          <div style={{ display: "flex", gap: 18, flexWrap: "wrap", alignItems: "center" }}>
            {editing ? (
              <>
                <Btn variant="ghost" dark={dark} onClick={() => update(desk.default_layout)}>Reset</Btn>
                <Btn variant="primary" dark={dark} icon="chk" onClick={() => setEditing(false)}>Done</Btn>
              </>
            ) : (
              <>
                <TextLink testid="desk-customize" onClick={() => setEditing(true)}>Customize</TextLink>
                {actions}
                {bookAction && <BookMediaLink href={bookShootHref({ user })} dark={dark} testId="desk-book" />}
              </>
            )}
          </div>
        </div>
      )}

      <Stats testid="desk-kpis" items={desk.kpis.slice(0, 3).map((k) => ({
        label: k.label,
        value: fmtKpi(k),
      }))} />

      <div style={{ display: "grid", gridTemplateColumns: wide ? "repeat(6, minmax(0, 1fr))" : "minmax(0, 1fr)", gap: 24 }}>
        {layout.map(([key, span], i) => {
          const W = RENDER[key];
          const data = desk.widgets[key];
          const off = (desk.unavailable || []).includes(key);
          return (
            <section key={key} aria-label={desk.titles[key]} style={{ gridColumn: wide ? `span ${span}` : "auto", position: "relative", background: p.bg,
              ...(editing ? { border: `1px dashed ${p.hairS}`, borderRadius: 2, padding: "var(--bx-pad)" } : { borderTop: `1px solid ${p.hair}`, paddingTop: 16 }), minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, minHeight: 26 }}>
                <h2 style={{ fontSize: 15, fontWeight: 500, letterSpacing: -0.2, margin: 0, color: p.ink }}>{desk.titles[key]}</h2>
                {editing && (
                  <div style={{ display: "flex", gap: 4 }}>
                    <IconBtn p={p} name="arrL" label="Move earlier" onClick={() => update(move(layout, i, -1))} disabled={i === 0} />
                    <IconBtn p={p} name="arr" label="Move later" onClick={() => update(move(layout, i, 1))} disabled={i === layout.length - 1} />
                    {wide && <IconBtn p={p} name="grid" label={`Width ${span} of 6, change`} onClick={() => update(cycleSpan(layout, i))} />}
                    <IconBtn p={p} name="x" label="Remove" onClick={() => update(removeAt(layout, i))} />
                  </div>
                )}
              </div>
              <div style={{ marginTop: 10 }}>
                {off ? <Empty p={p}>Couldn’t load this right now.</Empty> : W && data ? <W d={data} p={p} dark={dark} go={go} userId={user?.id} role={desk.role} /> : null}
              </div>
            </section>
          );
        })}
        {!layout.length && !editing && (
          <div style={{ gridColumn: wide ? "span 6" : "auto", fontSize: 13, color: p.ink3 }}>Your desk is empty. <span onClick={() => setEditing(true)} style={{ color: p.ink, cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 3 }}>Add widgets</span></div>
        )}
      </div>

      {editing && (
        <div style={{ border: `1px solid ${p.hair}`, borderRadius: 2, padding: "16px var(--bx-pad)" }}>
          <Over color={p.ink3}>Add widgets</Over>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 14 }}>
            {library.length ? library.map((k) => (
              <button key={k} type="button" onClick={() => update(addWidget(layout, k))}
                style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "8px 12px", border: `1px solid ${p.hair}`, borderRadius: 2,
                  fontSize: 12.5, color: p.ink2, background: p.bg, cursor: "pointer", fontFamily: F.display }}>
                <I name="plus" size={12} color={p.ink3} />{desk.titles[k]}
              </button>
            )) : <span style={{ fontSize: 12.5, color: p.ink3 }}>Every widget is on your desk.</span>}
          </div>
        </div>
      )}
    </div>
  );
}
